import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { getTokens } from "../../styles/tokens";

/* ------------------------------------------------------------------ */
/* Helpers de cor (locais, defensivos: aceitam hex ou objeto semântico) */
/* ------------------------------------------------------------------ */

const hexToRgb = (hex) => {
  if (typeof hex !== "string") return null;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length === 8) h = h.slice(0, 6);
  if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return null;
  const n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

export const alpha = (color, a) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${a})`;
};

const shade = (color, pct) => {
  const rgb = hexToRgb(color);
  if (!rgb) return color;
  const f = (c) =>
    Math.max(
      0,
      Math.min(255, Math.round(pct < 0 ? c * (1 + pct / 100) : c + (255 - c) * (pct / 100)))
    );
  const toHex = (c) => c.toString(16).padStart(2, "0");
  return `#${toHex(f(rgb.r))}${toHex(f(rgb.g))}${toHex(f(rgb.b))}`;
};

// Fallbacks (Tailwind emerald/amber/red/sky/slate) caso tokens.semantic não traga a cor.
const FALLBACK = {
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
  info: "#0EA5E9",
  neutral: "#94A3B8",
};

const STATUS_TONE = { success: "ok", warning: "warn", error: "down", neutral: "neutral" };

/**
 * Resolve um tom (`success|warning|error|info|neutral|brand`) em
 * `{ main, bg, fg }` a partir dos tokens. Entrada semântica pode ser
 * string (hex) ou objeto `{ main|base, tint|light|bg, text|dark|fg }`.
 */
export const resolveTone = (t, tone, mode) => {
  const dark = mode === "dark";
  if (tone === "brand") {
    return { main: t.brand, bg: t.tint.soft, fg: dark ? shade(t.brand, 25) : shade(t.brand, -20) };
  }
  // Tons do painel (ok/atenção/crítico/neutro): texto e fundo vêm prontos dos tokens
  const statusKey = STATUS_TONE[tone];
  if (statusKey && t.status && t.status[statusKey]) {
    const pair = t.status[statusKey];
    return { main: pair.fg, bg: pair.bg, fg: pair.fg };
  }
  if (tone === "neutral") {
    const main = t.textMuted || FALLBACK.neutral;
    return { main, bg: alpha(main, dark ? 0.18 : 0.14), fg: t.textSecondary };
  }
  const raw = (t.semantic && t.semantic[tone]) || FALLBACK[tone] || FALLBACK.neutral;
  const main = typeof raw === "string" ? raw : raw.main || raw.base || raw.color || FALLBACK[tone];
  const bg =
    typeof raw === "object" && (raw.tint || raw.light || raw.bg)
      ? raw.tint || raw.light || raw.bg
      : alpha(main, dark ? 0.18 : 0.1);
  const fg =
    typeof raw === "object" && (raw.text || raw.dark || raw.fg)
      ? raw.text || raw.dark || raw.fg
      : dark
      ? shade(main, 30)
      : shade(main, -25);
  return { main, bg, fg };
};

/** Mapa de status comuns do domínio → tom + rótulo (usado quando só `status` é passado). */
const STATUS_MAP = {
  // conexões WhatsApp
  CONNECTED: { tone: "success", label: "Conectado" },
  OPEN: { tone: "success", label: "Aberto" },
  DISCONNECTED: { tone: "error", label: "Desconectado" },
  TIMEOUT: { tone: "error", label: "Tempo esgotado" },
  PAIRING: { tone: "warning", label: "Pareando" },
  qrcode: { tone: "warning", label: "QR Code" },
  OPENING: { tone: "neutral", label: "Abrindo" },
  // tickets / genéricos
  open: { tone: "success", label: "Aberto" },
  pending: { tone: "warning", label: "Pendente" },
  closed: { tone: "neutral", label: "Fechado" },
  paid: { tone: "success", label: "Pago" },
  overdue: { tone: "error", label: "Vencido" },
  active: { tone: "success", label: "Ativo" },
  inactive: { tone: "neutral", label: "Inativo" },
  enabled: { tone: "success", label: "Ativado" },
  disabled: { tone: "neutral", label: "Desativado" },
  online: { tone: "success", label: "Online" },
  offline: { tone: "neutral", label: "Offline" },
  // aliases diretos de tom
  success: { tone: "success" },
  warning: { tone: "warning" },
  error: { tone: "error" },
  info: { tone: "info" },
  neutral: { tone: "neutral" },
  brand: { tone: "brand" },
};

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      borderRadius: t.radius.badge,
      fontFamily: t.font,
      fontWeight: 600,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
      verticalAlign: "middle",
      maxWidth: "100%",
      border: "1px solid transparent",
      transition: `background-color ${t.motion.fast}, color ${t.motion.fast}, border-color ${t.motion.fast}`,
    },
    md: { fontSize: 12, padding: "2px 8px" },
    sm: { fontSize: 11, padding: "1px 8px" },
    lg: { fontSize: 13, padding: "4px 12px" },
    dot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      flex: "none",
    },
    label: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };
});

/**
 * Selo de status do painel: raio 6, 12px/600, fundo suave do tom e ponto 6px.
 *
 * Props:
 * - `status`: string do domínio (`CONNECTED`, `pending`, ...) → tom e rótulo automáticos.
 * - `color` (alias `tone`): `success|warning|error|info|neutral|brand` — sobrepõe o tom do status.
 * - `label`: texto; se omitido usa o rótulo do `status` (ou o próprio status).
 * - `dot`: mostra o ponto (default `true`).
 * - `size`: `sm|md|lg` (default `md`).
 * - `icon?`: nó opcional antes do texto (substitui o ponto).
 */
const StatusBadge = ({
  status,
  color,
  tone,
  label,
  dot = true,
  size = "md",
  icon,
  className,
  style,
  children,
  ...rest
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const t = getTokens(theme.palette.type, theme.palette.primary.main);

  const cfg = (status && STATUS_MAP[status]) || (status && STATUS_MAP[String(status).toLowerCase()]) || {};
  const resolvedTone = color || tone || cfg.tone || "neutral";
  const colors = resolveTone(t, resolvedTone, theme.palette.type);
  const text = label != null ? label : children != null ? children : cfg.label || status;

  return (
    <span
      className={[classes.root, classes[size] || classes.md, className].filter(Boolean).join(" ")}
      style={{
        backgroundColor: colors.bg,
        color: colors.fg,
        ...style,
      }}
      {...rest}
    >
      {icon ? (
        icon
      ) : dot ? (
        <span className={classes.dot} style={{ backgroundColor: colors.main }} />
      ) : null}
      <span className={classes.label}>{text}</span>
    </span>
  );
};

export default StatusBadge;
